import { useMemo, useState, type ReactElement } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  fetchPlans,
  formatUsd,
  type CreditTier,
  type PlanCatalogEntry,
} from "@/billing/api";

export type BillingCycle = "monthly" | "annual";

/**
 * Tier a customer lands on when they reach signup without picking one — the
 * bottom of the ladder. Mirrors `ENTRY_TIER_CREDITS` in the API's plan
 * catalog, which is what checkout actually falls back to.
 */
export const ENTRY_TIER_CREDITS = 100;

interface PricingProps {
  initialCycle?: BillingCycle;
  /** Heading shown above the cards (omit when used inside a route header). */
  showHeading?: boolean;
}

// The ladder is served by the API's plan catalog (`GET /credits/plans`) rather
// than duplicated here, so the price on this page is by construction the price
// Checkout will charge.
function usePlanCatalog() {
  return useQuery({
    queryKey: ["plan-catalog"],
    queryFn: fetchPlans,
    staleTime: 10 * 60 * 1000,
  });
}

const PRO_FEATURES = [
  "5 build credits a day on top of your monthly bundle",
  "Unused monthly credits roll over a month",
  "Custom domain and SSL",
  "Buy extra credits whenever you need them",
  "Up to 5GB media storage",
];

const BUSINESS_FEATURES = [
  "Everything in Pro",
  "Several sites on one balance",
  "Shared access for your team",
  "Priority support",
];

export function Pricing({
  initialCycle = "annual",
  showHeading = true,
}: PricingProps): ReactElement {
  const [cycle, setCycle] = useState<BillingCycle>(initialCycle);
  const [credits, setCredits] = useState(100);
  const { data, isPending, isError } = usePlanCatalog();

  const plans = useMemo(() => indexPlans(data?.plans), [data]);
  const proTiers = plans.pro?.tiers ?? [];
  const tier = proTiers.find((t) => t.monthlyCredits === credits) ?? proTiers[0];
  const businessTier = plans.business?.tiers.find(
    (t) => t.monthlyCredits === credits,
  );

  return (
    <section
      id="pricing"
      className={
        showHeading
          ? "border-y border-[#e2e5e2] py-24 sm:py-32"
          : "border-b border-[#e2e5e2] pt-8 pb-24 sm:pt-10 sm:pb-32"
      }
    >
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        {showHeading ? (
          <div className="mx-auto max-w-2xl text-center">
            <div className="marketing-kicker text-[#45635e]">Simple pricing</div>
            <h2 className="marketing-display mt-5 text-5xl leading-[1.02] text-[#12211d] sm:text-6xl">
              Pay for the work.{" "}
              <span className="block text-[#71817d]">Not for a seat.</span>
            </h2>
            <p className="mt-7 text-lg leading-8 text-[#63716d]">
              Every change you ask for costs credits. Start free, and pick a
              monthly bundle when you want more than the daily allowance.
            </p>
          </div>
        ) : null}

        <div className={showHeading ? "mt-12 flex justify-center" : "flex justify-center"}>
          <div className="relative">
            <Tabs
              value={cycle}
              onValueChange={(value) => setCycle(value as BillingCycle)}
            >
              <TabsList className="grid h-11 w-[220px] grid-cols-2 rounded-full border border-[#c9d6d1] bg-[#d7e2de] p-1">
                <TabsTrigger
                  value="monthly"
                  className="h-full cursor-pointer rounded-full px-3 text-xs text-[#5e6d69] shadow-none hover:text-[#12211d] data-[state=active]:bg-white data-[state=active]:text-[#12211d] data-[state=active]:shadow-[0_1px_3px_rgba(18,33,29,0.16)]"
                >
                  Monthly
                </TabsTrigger>
                <TabsTrigger
                  value="annual"
                  className="h-full cursor-pointer rounded-full px-3 text-xs text-[#5e6d69] shadow-none hover:text-[#12211d] data-[state=active]:bg-white data-[state=active]:text-[#12211d] data-[state=active]:shadow-[0_1px_3px_rgba(18,33,29,0.16)]"
                >
                  Annual
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <span className="pointer-events-none absolute top-full left-1/2 mt-2 -translate-x-1/2 whitespace-nowrap text-xs font-medium text-[#4d746d] sm:top-1/2 sm:left-full sm:mt-0 sm:ml-3 sm:translate-x-0 sm:-translate-y-1/2">
              2 months free
            </span>
          </div>
        </div>

        {isError ? (
          <p className="mt-10 text-center text-sm text-[#65736f]">
            Prices are taking a moment to load.{" "}
            <Link to="/signup" search={{ cycle }} className="underline">
              Start your site
            </Link>{" "}
            and you&apos;ll see them at checkout.
          </p>
        ) : null}

        <div className="mx-auto mt-10 grid max-w-4xl gap-6 lg:grid-cols-2 lg:items-start">
          <PlanCard
            name="Pro"
            {...cardPricing(tier, cycle, isPending, "$25")}
            per={cycle === "annual" ? "/month, billed yearly" : "/month"}
            blurb="For a site that changes often — new dates, new photos, new pages."
            features={PRO_FEATURES}
            cta="Choose Pro"
            ctaSearch={{ cycle, plan: "pro", credits }}
            variant="feature"
            creditPicker={
              <CreditPicker
                tiers={proTiers}
                value={credits}
                onChange={setCredits}
                disabled={isPending || proTiers.length === 0}
              />
            }
          />

          <PlanCard
            name="Business"
            {...cardPricing(businessTier, cycle, isPending, "$50")}
            per={cycle === "annual" ? "/month, billed yearly" : "/month"}
            blurb="Same credits, plus the controls a team needs."
            features={BUSINESS_FEATURES}
            cta="Choose Business"
            ctaSearch={{ cycle, plan: "business", credits }}
            variant="quiet"
          />
        </div>

        <p className="mx-auto mt-10 max-w-2xl text-center text-sm leading-6 text-[#65736f]">
          A credit is one piece of work — a text change, a new photo, a fresh
          page. Most small updates cost less than one, and every site gets 5
          build credits a day on top of its bundle. Run low and you can add more
          at any time; your site stays online either way.
        </p>
      </div>
    </section>
  );
}

function indexPlans(
  plans: PlanCatalogEntry[] | undefined,
): Partial<Record<PlanCatalogEntry["id"], PlanCatalogEntry>> {
  const out: Partial<Record<PlanCatalogEntry["id"], PlanCatalogEntry>> = {};
  for (const plan of plans ?? []) out[plan.id] = plan;
  return out;
}

/** Annual tiers are quoted per month so the two cycles compare directly. */
function priceLabel(tier: CreditTier, cycle: BillingCycle): string {
  const cents =
    cycle === "annual"
      ? Math.round(tier.annualPriceCents / 12)
      : tier.monthlyPriceCents;
  return formatUsd(cents);
}

function cardPricing(
  tier: CreditTier | undefined,
  cycle: BillingCycle,
  pending: boolean,
  fallback: string,
): { price: string; wasPrice?: string; footnote?: string } {
  if (!tier) return { price: pending ? "—" : fallback };
  if (cycle !== "annual") return { price: priceLabel(tier, cycle) };

  const savedCents = tier.monthlyPriceCents * 12 - tier.annualPriceCents;
  const annualMonthly = priceLabel(tier, "annual");
  const monthly = formatUsd(tier.monthlyPriceCents);
  return {
    price: annualMonthly,
    wasPrice: monthly === annualMonthly ? undefined : monthly,
    footnote:
      savedCents > 0
        ? `${formatUsd(tier.annualPriceCents)} charged once a year — ${formatUsd(savedCents)} less than monthly.`
        : `${formatUsd(tier.annualPriceCents)} charged once a year.`,
  };
}

interface CreditPickerProps {
  tiers: CreditTier[];
  value: number;
  onChange: (credits: number) => void;
  disabled: boolean;
}

function CreditPicker({
  tiers,
  value,
  onChange,
  disabled,
}: CreditPickerProps): ReactElement {
  const index = Math.max(
    0,
    tiers.findIndex((t) => t.monthlyCredits === value),
  );

  return (
    <div className="mt-6 rounded-2xl bg-[#f4f8f6] p-4">
      <div className="flex items-baseline justify-between">
        <label
          htmlFor="credit-tier"
          className="text-xs font-semibold uppercase tracking-[0.14em] text-[#41655e]"
        >
          Credits a month
        </label>
        <span className="text-sm font-medium text-[#14231f]">
          {value.toLocaleString()}
        </span>
      </div>
      <input
        id="credit-tier"
        type="range"
        min={0}
        max={Math.max(0, tiers.length - 1)}
        step={1}
        value={index}
        disabled={disabled}
        onChange={(event) => {
          const next = tiers[Number(event.currentTarget.value)];
          if (next) onChange(next.monthlyCredits);
        }}
        className="mt-3 w-full accent-[#b8b9dd]"
        aria-label="Credits per month"
      />
      <div className="mt-1 flex justify-between text-[11px] text-[#7a8783]">
        <span>{tiers[0]?.monthlyCredits.toLocaleString() ?? "100"}</span>
        <span>
          {tiers[tiers.length - 1]?.monthlyCredits.toLocaleString() ?? "10,000"}
        </span>
      </div>
    </div>
  );
}

interface PlanCardProps {
  name: string;
  price: string;
  /** Monthly list price, shown struck through against the annual quote. */
  wasPrice?: string;
  per: string;
  blurb: string;
  features: string[];
  cta: string;
  ctaSearch: {
    cycle: BillingCycle;
    plan?: "pro" | "business";
    credits?: number;
  };
  variant: "feature" | "quiet";
  creditPicker?: ReactElement;
  footnote?: string;
}

function PlanCard({
  name,
  price,
  wasPrice,
  per,
  blurb,
  features,
  cta,
  ctaSearch,
  variant,
  creditPicker,
  footnote,
}: PlanCardProps): ReactElement {
  const featured = variant === "feature";
  return (
    <div
      className={
        featured
          ? "overflow-hidden rounded-[2rem] border-2 border-[#b8b9dd] bg-white"
          : "overflow-hidden rounded-[2rem] border border-[#dce4e0] bg-white"
      }
    >
      <div className="p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#41655e]">
            {name}
          </div>
          {featured ? (
            <span className="rounded-full bg-[#e2f2ee] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#28645b]">
              Most popular
            </span>
          ) : null}
        </div>

        <div className="mt-5 flex flex-wrap items-end gap-x-2 gap-y-1">
          <span className="marketing-display text-5xl leading-none text-[#14231f] sm:text-6xl">
            {price}
          </span>
          {wasPrice ? (
            <s className="pb-1 text-lg text-[#8a9692]">{wasPrice}</s>
          ) : null}
          {per ? <span className="pb-1 text-sm text-[#65736f]">{per}</span> : null}
        </div>
        <p className="mt-3 text-sm leading-6 text-[#65736f]">{blurb}</p>

        {creditPicker}

        <ul className="mt-7 grid gap-3">
          {features.map((feature) => (
            <li
              key={feature}
              className="flex items-start gap-2.5 text-sm leading-6 text-[#334641]"
            >
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#e2f2ee] text-[#28645b]">
                <Check className="h-3 w-3" aria-hidden="true" />
              </span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <Button
          asChild
          size="lg"
          className={
            featured
              ? "mt-8 h-12 w-full rounded-full bg-[#dceffc] text-sm text-[#20272b] shadow-none hover:bg-[#c8e6fa]"
              : "mt-8 h-12 w-full rounded-full border border-[#c7e2f5] bg-white text-sm text-[#20272b] shadow-none hover:bg-[#f3f9fe]"
          }
        >
          <Link to="/signup" search={ctaSearch}>
            {cta}
          </Link>
        </Button>
        <p className="mt-4 text-center text-xs text-[#65736f]">
          {footnote ?? "14-day free trial on paid plans. Cancel anytime."}
        </p>
      </div>
    </div>
  );
}
